// Modules:
import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
    useContext,
} from 'react';
import { MapContainer } from 'react-leaflet';
import * as esri from 'esri-leaflet';
import L from 'leaflet';

// Utils:
import TopicConfigContext from 'utilities/topicConfigContext';

// Components:
import Grid from '@mui/material/Grid';
import Slider from '@mui/material/Slider';
import GeometryToggleCard from '../Map/GeometryToggleCard';
import LayerComp from '../Map/LayerComp';
import LegendList from '../Map/LegendList';
import LeafletControlGeocoder from '../Map/LeafletControlGeocoder';

const MapContent = ({
    mapRef,
    mapLayers,
    mapZoom,
    defaultMapZoom,
    stateBounds,
    selectedLayerSet,
    topic,
    theme,
    tab,
    search,
    pathname,
    routeToLayers,
    queryStringParse,
    setMapLayers,
    setMapZoom,
    setTableData,
    setLayersLoading,
    setMapDemographicContent,
    setChartDemographicContent,
    setMissingSuppressedModalOpen,
    styles,
}) => {
    // // State // //
    const [timeSliderMarks, setTimeSliderMarks] = useState(null);
    const [timeSliderMinMax, setTimeSliderMinMax] = useState({
        min: null,
        max: null,
    });
    // Setting the slider value to 0 for use within the 'value' prop of the time slider.
    // The 'Value' prop requires a number and 0 sets it to the left most value of the slider.
    const [sliderValue, setSliderValue] = useState(0);

    // // Context // //
    const { tab: currentTab } = useContext(TopicConfigContext);

    // // Refs // //
    // Need to use refs for the loadLayers function, otherwise react will lose reference to the data on update
    const sourceDataRef = useRef(null);
    const selectedLayerRef = useRef(selectedLayerSet);
    const currentTabRef = useRef(currentTab.tabPath);
    const timeSliderActiveLayerRef = useRef(null);

    // // Methods // //

    // Custom hook to debounce slider onchange method:
    const useDebounce = (value, milliSeconds) => {
        const [debouncedValue, setDebouncedValue] = useState(value);

        useEffect(() => {
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, milliSeconds || 450); // If no milliSeconds provided default to 450.

            return () => {
                clearTimeout(handler);
            };
        }, [value, milliSeconds]);

        return debouncedValue;
    };

    // Debouncing the slider events to prevent rapid slider updates breaking the leaflet map:
    // 450 ms delay is enough time for the map to complete its updates before allowing another slider change
    const debouncedSliderValue = useDebounce(sliderValue, 450);

    const getUpdatedMapLayers = (existingLayers, visibleLayers) =>
        // If layer that should be visible is already in state variable "mapLayers", grab it and return it. If not, return a new visibleLayer.
        // NOTE: This function prevents unnecessary reloading of layers that are already active
        visibleLayers.map(
            (visibleLayer) =>
                existingLayers.find(
                    (existingLayer) => existingLayer.id === visibleLayer.id
                ) || visibleLayer
        );

    // Set the features within the extent format to match what the attribute table expects
    const formatTableLayer = (extent) => {
        const { options, featuresWithinExtent } = extent;
        const {
            tableTitle,
            outFields,
            columnHeaders,
        } = selectedLayerRef.current;
        const id = options;

        const features = Object.values(featuresWithinExtent).map((item) => {
            // Filter the raw data for our desired fields, defined in "outFields" in topics.js:
            const filteredFields = outFields?.reduce(
                (obj, key) => ({
                    ...obj,
                    [key]: item.feature.properties[key],
                }),
                {}
            );
            return filteredFields;
        });

        const formattedTableData = {
            id,
            info: {
                ...options,
                tableTitle,
                columnHeaders,
            },
            features: [...features],
        };

        setTableData([formattedTableData]);
    };

    // Set what features in a map layer are within the active extent
    const setLayerExtent = () => {
        const featuresWithinExtent = [];

        if (sourceDataRef.current) {
            // eslint-disable-next-line no-underscore-dangle
            Object.values(sourceDataRef.current._layers).forEach((layer) => {
                // For point layer. TODO: this may be deleted if not needed, unsure if we'll have point layer data
                if (layer.feature.geometry.type.toLowerCase() === 'point') {
                    if (
                        mapRef.current
                            .getBounds()
                            .contains(
                                L.GeoJSON.coordsToLatLng(
                                    layer.feature.geometry.coordinates
                                )
                            )
                    ) {
                        featuresWithinExtent.push(layer);
                    }
                } else if (
                    layer.feature.geometry.type.toLowerCase() === 'polygon' ||
                    layer.feature.geometry.type.toLowerCase() === 'multipolygon'
                ) {
                    // Set depth for latlng conversion. Multipolygons should be 2.
                    const layerDepth =
                        layer.feature.geometry.type.toLowerCase() === 'polygon'
                            ? 1
                            : 2;

                    try {
                        const bounds = L.latLngBounds(
                            L.GeoJSON.coordsToLatLngs(
                                layer.feature.geometry.coordinates,
                                layerDepth
                            )
                        );

                        if (mapRef.current.getBounds().intersects(bounds)) {
                            featuresWithinExtent.push(layer);
                        }
                    } catch (e) {
                        console.error(
                            'Unable to determine feature bounds: ',
                            e.message
                        );
                    }
                }
            });

            formatTableLayer({
                featuresWithinExtent,
                options: sourceDataRef.current.options,
            });
        }
    };

    const setMapRef = (map) => {
        mapRef.current = map.target;

        const worldTopo = L.tileLayer(
            'https://services.arcgisonline.com/arcgis/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
            {
                attribution:
                    '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
            }
        ).addTo(mapRef.current); // Adding this layer to the map here sets it to be on by defualt in the layerControl.

        const lightGray = L.tileLayer(
            'https://services.arcgisonline.com/arcgis/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
            {
                attribution:
                    '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
            }
        );

        const baseMaps = {
            'World Topo': worldTopo,
            'Light Gray': lightGray,
        };

        const layerControl = L.control.layers(baseMaps, null, {
            position: 'bottomright',
        });

        layerControl.addTo(mapRef.current);

        mapRef.current.on('zoomend moveend', () => {
            setLayerExtent();
            setMapZoom(map.target.getZoom());
        });
    };

    // Cleanup function, memoized through useCallback in order to avoid infinite re-renders when passed into useEffect dependency array:
    const cleanupLeaflet = useCallback(() => {
        if (mapRef.current) {
            mapRef.current.off();
            setMapLayers([]);
        }
        setMapZoom(defaultMapZoom);
        setTableData([]);
    }, [mapRef, defaultMapZoom, setMapZoom, setMapLayers, setTableData]);

    const cleanupTimeSlider = useCallback(() => {
        setSliderValue(0);
        setTimeSliderMarks(null);
    }, []);

    // // Effects // //

    // If layer set changes, reset table data to empty array (should repopulate with new selectedLayerSet's source layer data when loadLayers fires)
    useEffect(() => setTableData([]), [selectedLayerSet, setTableData]);

    // Clean up map content when switching between time slider maps beneath the same tab:
    useEffect(() => cleanupTimeSlider(), [cleanupTimeSlider, currentTab]);

    // Cleanup open demographics content when switching over from a chart component for both the chart and map popup state:
    useEffect(() => {
        setMapDemographicContent(null);
        setChartDemographicContent(null);
    }, [setChartDemographicContent, setMapDemographicContent]);

    // Load a new set of layers if any part of the url changes:
    // linter wants unchanging useState setter functions (passed as a prop to this comp) to be included in the deps array, will disable warning here:
    useEffect(() => {
        const loadLayers = () => {
            // Reset layers loading to an empty array:
            setLayersLoading([]);

            // Updates this component's ref to keep in line with prop
            selectedLayerRef.current = selectedLayerSet;

            const determineVisibility = (layer) => {
                // if layer is fixed, turn it on:
                if (layer.fixed) {
                    return true;
                }

                // If layer is toggleable, checking if search param for layers has been passed in and switching to that layer
                if (search.match('layers')) {
                    const { layers } = queryStringParse();
                    return layers.match(layer.id);
                }
                // If no params, will follow default setting (all tabs must have at least one "defaultVisible" layer per "setLayers" array in the config/topics.js file)
                return layer.defaultVisible;
            };

            const visibleLayers = selectedLayerSet.setLayers
                .filter(determineVisibility)
                .map((setLayer) => {
                    // Create esri layer and add event listeners for each layer that should be visible:
                    const esriLayer = esri.featureLayer({
                        ...setLayer,
                    });
                    return {
                        id: setLayer.id,
                        esriLayer,
                    };
                });

            // Construct marks object and min max for time slider:
            if (currentTab.mapSettings.timeSlider) {
                if (timeSliderMarks === null) {
                    const marks = selectedLayerSet.setLayers.map((layer) => {
                        // layer year is the last param in the url
                        const markYear = layer.url.substr(
                            layer.url.lastIndexOf('/') + 1
                        );

                        return {
                            value: Number(markYear),
                            label: markYear,
                        };
                    });

                    // Min, max values are the first and last items of our marks object
                    // as those should correspond to the lowest and highest years of our time slider span.
                    const minYear = marks[0].value;
                    const maxYear = marks[marks.length - 1].value;

                    // Grabbing the first map layer becuase we only want to display a single
                    // layer at a time, and on page render that should be the layer with the lowest year:
                    const firstMapLayer = visibleLayers[0];

                    setMapLayers([firstMapLayer]);
                    timeSliderActiveLayerRef.current = firstMapLayer;

                    setTimeSliderMarks([...marks]);
                    setTimeSliderMinMax((previousMinMax) => ({
                        ...previousMinMax,
                        min: minYear,
                        max: maxYear,
                    }));
                }
            } else {
                // Set active layers in HealthTopic state:
                setMapLayers((prevMapLayers) =>
                    // Determine which layers are already loaded and which layers are already visible:
                    getUpdatedMapLayers(prevMapLayers, visibleLayers)
                );
            }
        };
        loadLayers();
    }, [
        topic,
        theme,
        tab,
        search,
        queryStringParse,
        selectedLayerSet,
        selectedLayerRef,
        sourceDataRef,
        setLayersLoading,
        setMapLayers,
        currentTab.mapSettings.timeSlider,
        timeSliderMarks,
    ]);

    // Effect for watching slider value change and respective debouncing method:
    useEffect(() => {
        // Check if there has been a change in slider value:
        if (
            debouncedSliderValue &&
            currentTabRef.current === currentTab.tabPath
        ) {
            // The ref here avoids extraneous fetches if the slider is moved but then returned to its original
            // starting value before an event triggers.
            if (
                timeSliderActiveLayerRef.current.esriLayer.options
                    .sliderMarkValue !== debouncedSliderValue
            ) {
                const foundMapLayer = currentTab.mapSettings.mapSets[0].setLayers.find(
                    (mapSet) => {
                        const mapUrl = mapSet.url;
                        const layerYear = Number(
                            mapUrl.substr(mapUrl.lastIndexOf('/') + 1)
                        );

                        return layerYear === debouncedSliderValue;
                    }
                );

                if (foundMapLayer) {
                    const esriLayer = {
                        id: foundMapLayer.id,
                        esriLayer: esri.featureLayer({
                            ...foundMapLayer,
                        }),
                    };

                    setMapLayers([esriLayer]);
                    timeSliderActiveLayerRef.current = esriLayer;
                }
            }
        }
        currentTabRef.current = currentTab.tabPath;
    }, [
        currentTab.mapSettings.mapSets,
        currentTab.tabPath,
        debouncedSliderValue,
        setMapLayers,
    ]);

    // Define cleanup effect used only for unmount:
    // linter wants unchanging useState setter functions (passed as a prop to this comp) to be included in the deps array, will disable warning here:
    useEffect(() => cleanupLeaflet, [cleanupLeaflet]);

    // // Render // //
    return (
        <>
            <Grid item xs={12}>
                <MapContainer
                    bounds={stateBounds}
                    zoom={defaultMapZoom}
                    zoomSnap={0.5}
                    zoomControl={false}
                    className="webmap"
                    whenReady={setMapRef}
                >
                    <LeafletControlGeocoder
                        currentTab={currentTab}
                        stateBounds={stateBounds}
                        defaultMapZoom={defaultMapZoom}
                    />
                    {currentTab.mapSettings.mapSets.length > 1 ? (
                        <GeometryToggleCard
                            currentTab={currentTab}
                            queryStringParse={queryStringParse}
                        />
                    ) : null}
                    {mapLayers.map((layer) => (
                        <LayerComp
                            key={layer.esriLayer.options.id}
                            mapLayers={mapLayers}
                            layer={layer}
                            selectedLayerSet={selectedLayerSet}
                            mapZoom={mapZoom}
                            setMapLayers={setMapLayers}
                            sourceDataRef={sourceDataRef}
                            setLayerExtent={setLayerExtent}
                            setLayersLoading={setLayersLoading}
                            setMapDemographicContent={setMapDemographicContent}
                            currentTab={currentTab}
                        />
                    ))}
                </MapContainer>
                {currentTab.mapSettings.timeSlider && (
                    <div className="map-time-slider-container">
                        <Slider
                            aria-label="Year values"
                            defaultValue={0}
                            value={sliderValue}
                            min={timeSliderMinMax.min || null}
                            max={timeSliderMinMax.max || null}
                            step={null}
                            marks={timeSliderMarks || null}
                            onChange={(e) => setSliderValue(e.target.value)}
                        />
                    </div>
                )}
            </Grid>
            <Grid item xs={12}>
                <LegendList
                    layers={
                        currentTab.mapSettings.timeSlider
                            ? selectedLayerSet.setLayers.slice(0, 1) // Time slider legend is the same for all map layers, so we can just use the first entry in setLayers.
                            : selectedLayerSet.setLayers
                    }
                    activeLayers={mapLayers.map((layer) => layer.id)}
                    mapZoom={mapZoom}
                    pathname={pathname}
                    topic={topic}
                    queryStringParse={queryStringParse}
                    routeToLayers={routeToLayers}
                    setMissingSuppressedModalOpen={
                        setMissingSuppressedModalOpen
                    }
                    styles={styles}
                    currentTab={currentTab}
                />
            </Grid>
        </>
    );
};

export default MapContent;
